import { useCallback, useEffect } from 'react';
import * as amplitude from '@amplitude/analytics-browser';
import { useUser } from '@clerk/clerk-react';
import { getEnvironment } from '../utils/getEnvironment';

const AmplitudeApiKey = process.env.REACT_APP_AMPLITUDE_API_KEY;

/**
 * Hook to track page views whenever route changes
 * Should only be used in authenticated components
 */
const useAmplitude = () => {
  const { Identify } = amplitude;
  const { user, isSignedIn } = useUser();

  const initAmplitude = useCallback(async () => {
    try {
      if (!AmplitudeApiKey) {
        console.warn('Amplitude API key not found');
        return false;
      }
  
      amplitude.init(AmplitudeApiKey, {
        autocapture: {
          attribution: true,
          pageViews: true,
          sessions: true,
          formInteractions: true,
          fileDownloads: true,
          elementInteractions: true,
        },
      });

      return true;
    } catch (error) {
      console.error('Failed to initialize Amplitude:', error);
      return false;
    }
  }, []);

  const setUserIdentity = useCallback(async () => {
    try {
      if (!isSignedIn || !user) {
        console.warn('User not signed in or not loaded');
        return;
      }

      const identify = new Identify();
      identify.set('environment', getEnvironment());
      identify.setOnce('initial_user_role', user.publicMetadata?.role || 'anonymous');
      identify.set('user_role', user.publicMetadata?.role || 'anonymous');

      await amplitude.setUserId(user.id);
      await amplitude.identify(identify);

    } catch (error) {
      console.error('Failed to set user identity:', error);
    }
  }, [Identify, isSignedIn, user]);

  useEffect(() => {
    setUserIdentity();
  }, [setUserIdentity]);

  const trackEvent = useCallback(async (eventName, eventProperties) => {
    try {
      // Don't track if user isn't loaded yet or not signed in
      if (!isSignedIn || !user) {
        return;
      }

      // Check if Amplitude is initialized
      if (!amplitude.AmplitudeBrowser) {
        console.warn('Amplitude not initialized, skipping page view tracking');
        return;
      }

      await amplitude.track(eventName, eventProperties);
    } catch (error) {
      console.error('Failed to track event:', error);
    }
  }, [isSignedIn, user]);

  return {
    initAmplitude,
    trackEvent,
  };
};

export default useAmplitude;
